import React, { useState, useContext, useEffect, useRef } from 'react';
import { Button, IconButton, Tooltip, Typography } from '@mui/material';
import FormatSizeIcon from '@mui/icons-material/FormatSize';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ContrastIcon from '@mui/icons-material/Contrast';
import HeadsetIcon from '@mui/icons-material/Headset';
import HeadsetOffIcon from '@mui/icons-material/HeadsetOff';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import './Accessibility.css';
import StoreContext from './react-core-lib/components/app/store-context';
import { AuthState } from './stores/auth-store';

const ACCESSIBILITY_TOGGLE_EVENT = 'cetra:toggle-accessibility';
const DESKTOP_PANEL_WIDTH = 212;
const MOBILE_PANEL_WIDTH = 150;
const WhiteTooltip = styled(({ className, ...props }) => <Tooltip {...props} classes={{ popper: className }} />)(
    ({ theme }) => ({
        [`& .MuiTooltip-tooltip`]: {
            backgroundColor: '#fff',
            color: 'rgba(0, 0, 0, 0.87)',
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
            fontSize: 14,
        },
    }),
);

const letterSpacingLevels = [0, 1.68, 3.36, 5.04];

const AccessibilityOptions = ({
    fontSizeScale,
    letterSpacing,
    setLetterSpacing,
    handleZoomIn,
    handleZoomOut,
    handleFontSizeIncrease,
    handleFontSizeDecrease,
    handleClose,
    zoom,
    isHighContrast,
    setHighContrast,
}) => {
    const isZoomInDisabled = zoom >= 2.7;
    const isZoomOutDisabled = zoom <= 0.1;
    const isFontSizeIncreaseDisabled = fontSizeScale >= 2;
    const isFontSizeDecreaseDisabled = fontSizeScale <= 0.05;
    const [isReading, setIsReading] = useState(false);
    const [utterance, setUtterance] = useState(null);

    const handleScreenReaderClick = () => {
        if (!isReading) {
            const content = document.getElementById('main-content').innerText;
            const newUtterance = new SpeechSynthesisUtterance(content);
            newUtterance.onend = () => {
                setIsReading(false);
            };
            window.speechSynthesis.speak(newUtterance);
            setIsReading(true);
            setUtterance(newUtterance);
        } else {
            window.speechSynthesis.cancel();
            setIsReading(false);
            setUtterance(null);
        }
    };

    useEffect(() => {
        if (utterance && !isReading) {
            window.speechSynthesis.cancel();
            setUtterance(null);
        }
    }, [isReading, utterance]);

    return (
        <div className="accessibility-panel">
            <div className="accessibility-panel-header">
                <Typography className="accessibility-panel-title">Accessibility menu</Typography>
                <IconButton onClick={handleClose} className="accessibility-close-btn">
                    <CloseIcon />
                </IconButton>
            </div>
            <div className="accessibility-panel-actions">
            <WhiteTooltip title="Increase font size" arrow placement="left">
                <Button
                    onClick={handleFontSizeIncrease}
                    startIcon={<FormatSizeIcon />}
                    fullWidth
                    disabled={isFontSizeIncreaseDisabled}
                    className="accessibility-action-btn"
                >
                    Bigger text
                </Button>
            </WhiteTooltip>
            <WhiteTooltip title="Decrease font size" arrow placement="left">
                <Button
                    onClick={handleFontSizeDecrease}
                    startIcon={<FormatSizeIcon />}
                    fullWidth
                    disabled={isFontSizeDecreaseDisabled}
                    className="accessibility-action-btn"
                >
                    Smaller text
                </Button>
            </WhiteTooltip>
            <WhiteTooltip title="Toggle text spacing" arrow placement="left">
                <Button
                    onClick={() =>
                        setLetterSpacing(
                            letterSpacingLevels[
                                (letterSpacingLevels.indexOf(letterSpacing) + 1) % letterSpacingLevels.length
                            ],
                        )
                    }
                    startIcon={<TextFieldsIcon />}
                    fullWidth
                    className="accessibility-action-btn"
                >
                    Text spacing
                </Button>
            </WhiteTooltip>
            <WhiteTooltip title="Zoom in" arrow placement="left">
                <Button
                    onClick={handleZoomIn}
                    startIcon={<ZoomInIcon />}
                    fullWidth
                    disabled={isZoomInDisabled}
                    className="accessibility-action-btn"
                >
                    Zoom In
                </Button>
            </WhiteTooltip>
            <WhiteTooltip title="Zoom out" arrow placement="left">
                <Button
                    onClick={handleZoomOut}
                    startIcon={<ZoomOutIcon />}
                    fullWidth
                    disabled={isZoomOutDisabled}
                    className="accessibility-action-btn"
                >
                    Zoom out
                </Button>
            </WhiteTooltip>
            <WhiteTooltip title={isReading ? 'Stop screen reader' : 'Start screen reader'} arrow placement="left">
                <Button
                    onClick={handleScreenReaderClick}
                    startIcon={isReading ? <HeadsetOffIcon /> : <HeadsetIcon />}
                    fullWidth
                    className="accessibility-action-btn"
                >
                    {isReading ? 'Stop screen reader' : 'Start screen reader'}
                </Button>
            </WhiteTooltip>
            <WhiteTooltip title="Toggle high contrast Mode" arrow placement="left">
                <Button
                    onClick={() => setHighContrast(!isHighContrast)}
                    startIcon={<ContrastIcon />}
                    fullWidth
                    className="accessibility-action-btn"
                >
                    {isHighContrast ? 'Disable high contrast' : 'Enable high contrast'}
                </Button>
            </WhiteTooltip>
            </div>
        </div>
    );
};

const AccessibilityWidget = () => {
    const [showAccessibilityModal, setShowAccessibilityModal] = useState(false);
    const [fontSizeScale, setFontSizeScale] = useState(1); // Scale factor 1 (default)
    const [letterSpacing, setLetterSpacing] = useState(0);
    const [zoom, setZoom] = useState(1);
    const [isHighContrast, setHighContrast] = useState(false);
    const [panelPosition, setPanelPosition] = useState({ top: 0, left: 0, isMobile: false });
    const panelRef = useRef(null);
    const { authStore } = useContext(StoreContext);
    const isIframe = authStore.authState === AuthState.IFRAME_AUTHENTICATED;

    useEffect(() => {
        // Accessibility menu should stay closed by default until explicitly opened.
        setShowAccessibilityModal(false);
    }, []);

    useEffect(() => {
        document.documentElement.style.setProperty('--custom-font-size-scale', fontSizeScale);
        document.documentElement.style.setProperty('--custom-letter-spacing', `${letterSpacing}px`);
        document.documentElement.style.setProperty('--custom-zoom', zoom);
        document.documentElement.style.setProperty('--custom-high-contrast', isHighContrast ? '1' : '0');
        document.documentElement.classList.toggle('high-contrast', isHighContrast);
        document.documentElement.classList.toggle('large-font', fontSizeScale > 1);
        const mainContent = document.getElementById('main-content');
        if (mainContent) {
            mainContent.style.zoom = zoom;
        }
    }, [fontSizeScale, letterSpacing, zoom, isHighContrast]);

    useEffect(() => {
        const handleAccessibilityToggle = (event) => {
            const triggerRect = event?.detail?.triggerRect;
            const isMobile = !!event?.detail?.isMobile;

            if (!triggerRect) {
                return;
            }

            const panelWidth = isMobile ? MOBILE_PANEL_WIDTH : DESKTOP_PANEL_WIDTH;
            const viewportWidth = window.innerWidth;
            const maxLeft = viewportWidth - panelWidth - 8;
            // Align panel left edge to trigger icon left edge.
            const preferredLeft = triggerRect.left;
            const left = Math.min(Math.max(preferredLeft, 8), Math.max(maxLeft, 8));
            const top = triggerRect.bottom + 8;

            setPanelPosition({ top, left, isMobile });
            setShowAccessibilityModal((prev) => !prev);
        };

        window.addEventListener(ACCESSIBILITY_TOGGLE_EVENT, handleAccessibilityToggle);
        return () => {
            window.removeEventListener(ACCESSIBILITY_TOGGLE_EVENT, handleAccessibilityToggle);
        };
    }, []);

    useEffect(() => {
        if (!showAccessibilityModal) {
            return undefined;
        }

        const handleClickOutside = (event) => {
            if (panelRef.current && panelRef.current.contains(event.target)) {
                return;
            }

            const triggerElement = event.target.closest('.accessibility-trigger');
            if (triggerElement) {
                return;
            }

            setShowAccessibilityModal(false);
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showAccessibilityModal]);

    const handleFontSizeIncrease = () => {
        setFontSizeScale((prevScale) => Math.min(prevScale + 0.2, 2)); // Increase by 0.2, limit to 2
    };

    const handleFontSizeDecrease = () => {
        setFontSizeScale((prevScale) => Math.max(prevScale - 0.2, 0.05)); // Decrease by 0.2, limit to 0.05
    };

    const handleCloseAccessibilityModal = () => {
        setShowAccessibilityModal(false);
    };

    const handleZoomIn = () => {
        setZoom((prevZoom) => Math.min(prevZoom + 0.2, 2.7)); // Increase by 0.2, limit to 2.7
    };

    const handleZoomOut = () => {
        setZoom((prevZoom) => Math.max(prevZoom - 0.2, 0.1)); // Decrease by 0.2, limit to 0.1
    };

    return (
        !isIframe && (
            <section id="accessibility-menu">
                {showAccessibilityModal && (
                    <div
                        ref={panelRef}
                        className={`accessibility-panel-container ${
                            panelPosition.isMobile ? 'accessibility-panel-mobile' : 'accessibility-panel-desktop'
                        }`}
                        style={{ top: panelPosition.top, left: panelPosition.left }}
                    >
                        <AccessibilityOptions
                            fontSizeScale={fontSizeScale}
                            handleFontSizeIncrease={handleFontSizeIncrease}
                            handleFontSizeDecrease={handleFontSizeDecrease}
                            letterSpacing={letterSpacing}
                            setLetterSpacing={setLetterSpacing}
                            handleZoomIn={handleZoomIn}
                            handleZoomOut={handleZoomOut}
                            handleClose={handleCloseAccessibilityModal}
                            zoom={zoom}
                            isHighContrast={isHighContrast}
                            setHighContrast={setHighContrast}
                        />
                    </div>
                )}
            </section>
        )
    );
};

export default AccessibilityWidget;
